import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UIState {
  sidebarCollapsed: boolean;
  /** 모바일(lg 미만) 사이드바 서랍 열림 — persist 하지 않음 */
  mobileSidebarOpen: boolean;
  breadcrumbs: Array<{ title: string; path?: string }>;
  /** 레거시 MainLayout #cover-spin — 중첩 호출용 카운터 */
  globalLoadingCount: number;
  globalLoadingTip: string | null;
}

interface UIActions {
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setMobileSidebarOpen: (open: boolean) => void;
  setBreadcrumbs: (
    breadcrumbs: Array<{ title: string; path?: string }>,
  ) => void;
  /** 전역 로딩 시작 (중첩 가능). tip은 마지막 호출 기준 */
  startGlobalLoading: (tip?: string) => void;
  stopGlobalLoading: () => void;
  /** 조건부 동기화용 — true면 count≥1, false면 0으로 리셋 */
  setGlobalLoading: (loading: boolean, tip?: string) => void;
}

type UIStore = UIState & UIActions;

export const useUIStore = create<UIStore>()(
  persist(
    (set) => ({
      // 초기 상태
      sidebarCollapsed: false,
      mobileSidebarOpen: false,
      breadcrumbs: [],
      globalLoadingCount: 0,
      globalLoadingTip: null,

      // 액션들
      toggleSidebar: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },

      setSidebarCollapsed: (collapsed: boolean) => {
        set({ sidebarCollapsed: collapsed });
      },

      setMobileSidebarOpen: (open: boolean) => {
        set({ mobileSidebarOpen: open });
      },

      setBreadcrumbs: (
        breadcrumbs: Array<{ title: string; path?: string }>,
      ) => {
        set({ breadcrumbs });
      },

      startGlobalLoading: (tip) => {
        set((state) => ({
          globalLoadingCount: state.globalLoadingCount + 1,
          globalLoadingTip: tip ?? state.globalLoadingTip ?? '처리 중...',
        }));
      },

      stopGlobalLoading: () => {
        set((state) => {
          const next = Math.max(0, state.globalLoadingCount - 1);
          return {
            globalLoadingCount: next,
            globalLoadingTip: next === 0 ? null : state.globalLoadingTip,
          };
        });
      },

      setGlobalLoading: (loading, tip) => {
        set({
          globalLoadingCount: loading ? 1 : 0,
          globalLoadingTip: loading ? (tip ?? '처리 중...') : null,
        });
      },
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
      }),
    },
  ),
);
